import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F8858SchdC1Service } from '../../services/f8858-schd-c1.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-f8858-schd-c1',
    templateUrl: './f8858-schd-c1.component.html',
    styleUrls: ['./f8858-schd-c1.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F8858SchdC1Component implements OnInit {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Flag to prevent duplicate init calls
  private isInitialized = false;
  private isLoading = false;

  @Input('base-urls')
  set _base_urls(baseUrls: string) {
    try {
      let urls: GtwElementsBaseUrlsModel = safeJsonInput<any>(baseUrls, null, 'f8858-schd-c1.parse1');
      if (Object.keys(urls).length > 0) {
        this.baseURLs = urls;
        this.tryInit();
      }
    } catch (e) {
      console.log("Input Not yet stable. Still waiting to get resolved.");
    }
  }
  baseURLs: GtwElementsBaseUrlsModel = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '' };

  filterParams: any = {};

  @Input('filter-params')
  set _filter_params(filterParams: string) {
    try {
      let params = safeJsonInput<any>(filterParams, null, 'f8858-schd-c1.parse2');
      if (Object.keys(params).length > 0) {
        this.filterParams = params;
        this.tryInit();
      }
    } catch (e) {
      console.log("Filter params input not yet stable. Still waiting to get resolved.");
    }
  }

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  @Input('header-data')
  set headerData(headerData: any) {
    try {
      this.globalService = safeJsonInput<any>(headerData, null, 'f8858-schd-c1.parse3');
      this.appService.USER_SETTINGS = this.globalService.userSettingsData;
      this.appService.SERVER_CONFIG = this.globalService.serverConfigData;
      this.GENERAL_CONFIG = this.globalService.generalConfigData;
      this.tryInit();
    } catch (e) {
      console.log("Input Not yet stable. Still waiting to get resolved.");
    }
  }

  // Row data from AngularJS
  rowData: any = {};

  @Input('row-data')
  set _rowData(rowData: string) {
    try {
      this.rowData = safeJsonInput<any>(rowData, null, 'f8858-schd-c1.parse4');
      this.tryInit();
    } catch (e) {
      console.log("Row data input not yet stable. Still waiting to get resolved.");
    }
  }

  // STANDARD COMPONENT PROPERTIES (SuperPrompt V8)
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Component properties migrated from F8858-schdC1Ctrl.js
  modalTitle = 'Form 8858 - Schedule C-1: Section 987 Gain or Loss Information';
  invalidSaveMessage = '';
  invalidInputMessage = '';
  invalidInputFlag = true;
  invalidSaveFlag = true;
  
  // Entity data properties
  entityList: any[] = [];
  parentLEName = '';
  currencyValue = '';
  currencyDesc = '';
  
  // Form data
  irsF8858ScheduleC1Data: any[] = [];
  originalData: any[] = [];
  currencies: any[] = [];
  filingGroup: any;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f8858SchdC1Service: F8858SchdC1Service,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Initial setup will be done via inputs
  }

  // Prevent duplicate init calls - only init when all required data is present
  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    
    // Check if all required data is present
    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    
    this.isInitialized = true;
    this.init();
  }

  init() {
    try {
      this.setupEntityData();
      this.loadFormData();
    } catch (e) {
      console.error('Error in F8858 Schedule C1 init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  private setupEntityData() {
    // Use REAL data from rowData - NO TEST_ values! (SuperPrompt V8)
    this.currencyValue = this.rowData.FUNCTIONAL_CURRENCY || '';
    this.parentLEName = (this.rowData.PARENT_LEID || '') + ' - ' + (this.rowData.PARENT_LE_NAME || '') + ' / ' + (this.rowData.PARENT_CDR_NO || '');

    // MANDATORY ORDER: Tax Year, LEID, LE Name (SuperPrompt V8)
    this.entityList = [
      { key: "Tax Year", value: this.rowData.TAX_YEAR || '' },
      { key: "LEID", value: this.rowData.LEID || '' },
      { key: "LE Name", value: this.rowData.LE_NAME || '' }
    ];
  }

  // Single request to load form data - combines all necessary data loading
  loadFormData() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;

    // Load currencies first for display
    this.loadCurrencies();

    const params = {
      action_code: 'hc0c3w',
      tax_year: this.rowData.TAX_YEAR || this.globalService?.globalParams?.tax_year,
      scenario: this.rowData.SCENARIO || this.globalService?.globalParams?.scenario,
      jcd_key: this.rowData.JCD_KEY || this.globalService?.globalParams?.jcd_key || 250,
      irs_form_no: 'F8858',
      combination_key: this.rowData.COMBINATION_KEY
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1' && data.jsonObject) {
          this.processFormData(data.jsonObject);
          this.originalData = _.cloneDeep(this.irsF8858ScheduleC1Data);
          console.log('Processed form data:', this.irsF8858ScheduleC1Data);
        } else {
          if (data.errorMessage === 'Access Denied') {
            this._snackBar.open('Sorry you do not have access to do the requested action.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('An error has occurred. If you continue to experience this issue please visit the help page to contact support.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading schedule C1 data:', error);
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadCurrencies() {
    const params = {
      action_code: '3ucwjb',
      tax_year: this.rowData.TAX_YEAR || this.filterParams.tax_year || this.globalService?.globalParams?.tax_year,
      jcd_key: this.rowData.JCD_KEY || this.filterParams.jcd_key || this.globalService?.globalParams?.jcd_key || 250
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1' && data.jsonObject) {
          this.currencies = data.jsonObject;
          // Find currency description
          for (let i = 0; i < this.currencies.length; i++) {
            if (this.currencies[i].CURR_CODE === this.currencyValue) {
              this.currencyDesc = this.currencies[i].CURR_DESC;
              break;
            }
          }
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        console.error('Error loading currencies:', error);
      }
    );
  }

  processFormData(irsF8858ScheduleC1Data: any[]) {
    this.irsF8858ScheduleC1Data = [];
    
    if (!irsF8858ScheduleC1Data || irsF8858ScheduleC1Data.length < 2) {
      return;
    }
    
    let i = 1;
    let previous_line_no = irsF8858ScheduleC1Data[i]?.line_no;

    while (i < irsF8858ScheduleC1Data.length) {
      let tempObj: any = {};
      let j = i;
      let count = 0;

      while (j < irsF8858ScheduleC1Data.length && previous_line_no === irsF8858ScheduleC1Data[j].line_no) {
        const attribValueParts = irsF8858ScheduleC1Data[j].attrib_value.split('~');
        const attribName = attribValueParts[0];
        // Convert PROPERTY_TYPE to string for consistent comparison
        const propertyType = String(irsF8858ScheduleC1Data[j].PROPERTY_TYPE);

        if (attribName === 'LINE_NO') {
          tempObj.propertyType = propertyType;
          tempObj.line_no = irsF8858ScheduleC1Data[j].line_no;
          tempObj.saveParams = {
            tax_year: irsF8858ScheduleC1Data[j].object_id.split('~')[0],
            trans_type_key: irsF8858ScheduleC1Data[j].object_id.split('~')[1],
            trans_Details_key: irsF8858ScheduleC1Data[j].object_id.split('~')[3],
            occurrence: irsF8858ScheduleC1Data[j].object_id.split('~')[4],
            form_key: irsF8858ScheduleC1Data[j].object_id.split('~')[6]
          };
        } else if (attribName === 'LINE_DESCRIPTION') {
          tempObj.line_description = attribValueParts[3];
        } else if (attribName === 'LINE_1_VALUE_8858_A') {
          tempObj.propertyType = propertyType;
          tempObj.line_description1 = attribValueParts[3];
          tempObj.value = {
            VALUE: irsF8858ScheduleC1Data[j].VALUE,
            line_attrib_key: attribValueParts[1],
            attrib_name: attribName
          };
        } else if (attribName === 'LINE_1_VALUE_8858_B') {
          tempObj.propertyType1 = propertyType;
          tempObj.line_description1 = attribValueParts[3];
          tempObj.value_1 = {
            VALUE: irsF8858ScheduleC1Data[j].VALUE,
            line_attrib_key: attribValueParts[1],
            attrib_name: attribName
          };
        } else if (attribName === 'LINE_2_VALUE_8858_A') {
          tempObj.propertyType = propertyType;
          tempObj.line_description1 = attribValueParts[3];
          tempObj.value = {
            VALUE: irsF8858ScheduleC1Data[j].VALUE,
            line_attrib_key: attribValueParts[1],
            attrib_name: attribName
          };
        } else if (attribName === 'LINE_2_VALUE_8858_B') {
          tempObj.propertyType1 = propertyType;
          tempObj.line_description1 = attribValueParts[3];
          tempObj.value_1 = {
            VALUE: irsF8858ScheduleC1Data[j].VALUE,
            line_attrib_key: attribValueParts[1],
            attrib_name: attribName
          };
        } else if (attribName === 'LINE_3_VALUE_8858_A') {
          tempObj.propertyType = propertyType;
          tempObj.line_description1 = attribValueParts[3];
          tempObj.value = {
            VALUE: irsF8858ScheduleC1Data[j].VALUE,
            line_attrib_key: attribValueParts[1],
            attrib_name: attribName
          };
        } else if (attribName === 'LINE_3_VALUE_8858_B') {
          tempObj.propertyType1 = propertyType;
          tempObj.line_description1 = attribValueParts[3];
          tempObj.value_1 = {
            VALUE: irsF8858ScheduleC1Data[j].VALUE,
            line_attrib_key: attribValueParts[1],
            attrib_name: attribName
          };
        } else {
          // Handle radio buttons (propertyType === '13') and other fields
          if (count > 0) {
            if (count === 1) {
              tempObj.attrib_name = [tempObj.attrib_name];
              tempObj.value = [tempObj.value];
            }
            tempObj.attrib_name[count] = attribName;
            tempObj.value[count] = {
              VALUE: irsF8858ScheduleC1Data[j].VALUE,
              line_attrib_key: attribValueParts[1]
            };
          } else {
            tempObj.propertyType = propertyType;
            tempObj.isEditableFlag = 'Y';
            tempObj.attrib_name = attribName;
            tempObj.value = {
              VALUE: irsF8858ScheduleC1Data[j].VALUE,
              line_attrib_key: attribValueParts[1]
            };
          }
        }

        previous_line_no = irsF8858ScheduleC1Data[j].line_no;
        // Use string comparison for propertyType
        if (propertyType === '13') {
          count++;
        }
        j++;
      }

      if (j < irsF8858ScheduleC1Data.length) {
        previous_line_no = irsF8858ScheduleC1Data[j].line_no;
      }

      this.irsF8858ScheduleC1Data.push(tempObj);
      i = j;
    }
  }

  validateInput(row: any, index: number) {
    // Mark row as changed
    if (row.value) {
      row.IS_CHANGED_FLAG = 'Y';
    }
    this.cdr.detectChanges();
  }

  onRadioBtnSelection(row: any, index: number, value: string) {
    if (Array.isArray(row.value)) {
      if (value === 'Y') {
        row.value[0].VALUE = 'Y';
        row.value[1].VALUE = null;
      } else if (value === 'N') {
        row.value[0].VALUE = null;
        row.value[1].VALUE = 'N';
      }
    }
    row.IS_CHANGED_FLAG = 'Y';
    this.cdr.detectChanges();
  }

  checkingForValidSave(tempData: any[]): boolean {
    this.invalidSaveFlag = true;
    this.invalidSaveMessage = 'Please correct the below information - ';

    if (tempData.length < 3) return this.invalidSaveFlag;

    if (tempData[0].value?.VALUE < 0 || tempData[0].value_1?.VALUE < 0) {
      this.invalidSaveFlag = false;
      this.invalidSaveMessage += ' Line 1 FDE/FB Amount or Recipient Amount should be debit balance. ';
      return this.invalidSaveFlag;
    }

    if (tempData[0].value?.VALUE > 0 && (tempData[0].value_1?.VALUE == 0 || tempData[0].value_1?.VALUE === undefined)) {
      this.invalidSaveFlag = false;
      this.invalidSaveMessage += ' Line 1 Recipient Amount is required. ';
      return this.invalidSaveFlag;
    }

    if (tempData[1].value?.VALUE > 0 && (tempData[1].value_1?.VALUE == 0 || tempData[1].value_1?.VALUE === undefined)) {
      this.invalidSaveFlag = false;
      this.invalidSaveMessage += ' Line 2 Recipient Amount is required. ';
      return this.invalidSaveFlag;
    }

    if (tempData[2].value?.VALUE > 0 && (tempData[2].value_1?.VALUE == 0 || tempData[2].value_1?.VALUE === undefined)) {
      this.invalidSaveFlag = false;
      this.invalidSaveMessage += ' Line 3 Recipient Amount is required. ';
      return this.invalidSaveFlag;
    }

    return this.invalidSaveFlag;
  }

  save() {
    // Prevent double-click
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }
    
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    // Validate save data
    this.checkingForValidSave(this.irsF8858ScheduleC1Data);

    // Check validation flags first
    if (!this.invalidInputFlag) {
      this._snackBar.open(this.invalidInputMessage, 'Close', {
        duration: 6000,
        panelClass: ['error-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }
    
    if (!this.invalidSaveFlag) {
      this._snackBar.open(this.invalidSaveMessage, 'Close', {
        duration: 6000,
        panelClass: ['error-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // Build save data - exactly like original AngularJS
    const editRow: any[] = [];
    const temporaryObject = this.irsF8858ScheduleC1Data;

    for (let loopIndex = 0; loopIndex < temporaryObject.length; loopIndex++) {
      // Process first 3 rows (lines 1-3) for value and value_1
      if (loopIndex < 3) {
        for (const key in temporaryObject[loopIndex]) {
          if (!temporaryObject[loopIndex].hasOwnProperty(key)) continue;

          const obj = temporaryObject[loopIndex][key];

          if (obj && typeof obj === 'object' && obj.hasOwnProperty('attrib_name')) {
            const value = obj.VALUE !== undefined ? obj.VALUE : 0;
            const lineAttribKey = obj.line_attrib_key;
            const attribName = obj.attrib_name;

            const saveObject = [{
              tax_year: this.rowData.TAX_YEAR || this.globalService?.globalParams?.tax_year,
              trans_type_key: temporaryObject[loopIndex].saveParams?.trans_type_key,
              combination_key: this.rowData.COMBINATION_KEY,
              group_obj_key: this.rowData.GROUP_OBJ_KEY,
              form_key: temporaryObject[loopIndex].saveParams?.form_key,
              line_no: temporaryObject[loopIndex].line_no,
              occurence: temporaryObject[loopIndex].saveParams?.occurrence === '0' ? null : temporaryObject[loopIndex].saveParams?.occurrence,
              line_attrib_key: lineAttribKey,
              Attribute_Name: attribName,
              Attribute_Value: value,
              trans_dtls_key: temporaryObject[loopIndex].saveParams?.trans_Details_key === '0' ? null : temporaryObject[loopIndex].saveParams?.trans_Details_key,
              trans_status: 'A'
            }];
            editRow.push(saveObject);
          }
        }
      }

      // Process radio button rows (propertyType === '13')
      if (temporaryObject[loopIndex].hasOwnProperty('propertyType') &&
        temporaryObject[loopIndex].propertyType === '13' &&
        Array.isArray(temporaryObject[loopIndex].attrib_name)) {
        for (let k = 0; k < temporaryObject[loopIndex].attrib_name.length; k++) {
          const saveObject = [{
            tax_year: this.rowData.TAX_YEAR || this.globalService?.globalParams?.tax_year,
            trans_type_key: temporaryObject[loopIndex].saveParams?.trans_type_key,
            combination_key: this.rowData.COMBINATION_KEY,
            group_obj_key: this.rowData.GROUP_OBJ_KEY,
            form_key: temporaryObject[loopIndex].saveParams?.form_key,
            line_no: temporaryObject[loopIndex].line_no,
            occurence: temporaryObject[loopIndex].saveParams?.occurrence === '0' ? null : temporaryObject[loopIndex].saveParams?.occurrence,
            line_attrib_key: temporaryObject[loopIndex].value[k].line_attrib_key,
            Attribute_Name: temporaryObject[loopIndex].attrib_name[k],
            Attribute_Value: temporaryObject[loopIndex].value[k].VALUE,
            trans_dtls_key: temporaryObject[loopIndex].saveParams?.trans_Details_key === '0' ? null : temporaryObject[loopIndex].saveParams?.trans_Details_key,
            trans_status: 'A'
          }];
          editRow.push(saveObject);
        }
      }
    }

    const scheduleSettings = [{
      tax_year: this.rowData.TAX_YEAR || this.globalService?.globalParams?.tax_year,
      scenario: this.rowData.SCENARIO || this.globalService?.globalParams?.scenario,
      jcd_key: this.rowData.JCD_KEY || this.globalService?.globalParams?.jcd_key || 250
    }];

    const scheduleC1Details = { addrows: [], editrows: editRow, removerows: [] };

    console.log('Saving Schedule C1 with data:', scheduleC1Details);

    this.f8858SchdC1Service.saveF8858ScheduleC1(
      this.GENERAL_CONFIG.base_url,
      scheduleSettings,
      scheduleC1Details,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Schedule C1 details successfully saved', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // Emit 'refresh' string for AngularJS modal close (SuperPrompt V8)
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error saving Schedule C1:', error);
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    this.irsF8858ScheduleC1Data = _.cloneDeep(this.originalData);
    this.cdr.detectChanges();
  }

  cancel() {
    // Emit cancel event to close modal (AngularJS integration - SuperPrompt V8)
    this.requestGridUpdate.emit('cancel');
  }

  // Track by function for ngFor
  itemTracker(index: number, item: any): any {
    return item ? item.line_no || index : index;
  }
}
